import { BackendTables, CallMetric, ConversationType, 
  CounterpartData, jsonSafeToSessionMetric, 
  jsonSafeToCounterpartData, jsonSafeToCallMetric, jsonSafeToSessionV2, jsonSafeToSessionDetailsV2, 
  SessionAgents, SessionDetailsV2, SessionMetric, SessionV2, jsonSafeToSessionAgents,
  PartyRole,
  CustomMetric,
  CustomDetail,
  jsonSafeToCustomMetric,
  jsonSafeToCustomDetail} from "./db"
import { 
  SessionV2Columns, 
  BackendColumns,
  ColumnValueMap,
} from "./table"
import { jsonSafeValueToDate } from "./typing"
import { jsonSafeToProspectInfo, ProspectInfo } from "./metadata"

export enum FilterOperator {
    EQ = 'EQ',
    NEQ = 'NEQ',
    GT = 'GT',
    GE = 'GE',
    LT = 'LT',
    LE = 'LE',
    IN = 'IN',
    NIN = 'NIN',
    LIKE = 'LIKE'
  }
  
  export type FilterColumn = {
    table: BackendTables,
    column_name: BackendColumns,
    operator: FilterOperator
    value_safe: any
  }
  
  export type SelectColumn = {
    table: BackendTables,
    column_name: BackendColumns
  }
 
  export type BackendFilter = FilterColumn[][]

  export type SessionListV5Params = {
    start?: Date | null,
    end?: Date | null,
    cnf: BackendFilter,
    select: SelectColumn[]
  }
  

export type SessionState = {
    session_id: string
    team_id: string
    started_at: Date
    states: {[key: string]: string}
}

export function statesStrToStates(states_str: string): {[key: string]: string} {
  // field_name_1:field_value_1|field_name_2:field_value_2|...|
  const states: {[key: string]: string} = {}
  
  if (!states_str || typeof states_str !== 'string') {
    return states
  }
  
  const state_pairs = states_str.split('|')
  for (const state_pair of state_pairs) {
    if (state_pair.trim()) {
      const [field_name, ...field_value_parts] = state_pair.split(':')
      const field_value = field_value_parts.join(':') // Rejoin in case the value contains colons
      if (field_name) {
        states[field_name.trim()] = field_value ? field_value.trim() : ''
      }
    }
  }
  return states
}

export function jsonSafeToSessionState(value: Record<string, any>): SessionState {
  return {
    session_id: value.session_id,
    team_id: value.team_id,
    started_at: jsonSafeValueToDate(value.started_at),
    states: statesStrToStates(value.state_str)
  }
}

  /**
   * A strongly typed Map class that provides type inference based on column key
   * This ensures that when using .get(KEY), TypeScript infers the correct return type
   */
  export class TypedResultMap extends Map<BackendColumns, any> {
    /**
     * Get a value from the map with proper type inference based on the column key
     * @param key The column key to retrieve
     * @returns The value with its proper type or undefined if not found
     */
    get<K extends BackendColumns>(key: K): ColumnValueMap[K] | undefined {
      return super.get(key) as ColumnValueMap[K] | undefined;
    }
    
    /**
     * Set a value in the map with type checking based on the column key
     * @param key The column key
     * @param value The value to set
     * @returns The map instance for chaining
     */
    set<K extends BackendColumns>(key: K, value: ColumnValueMap[K]): this {
      return super.set(key, value);
    }

    /**
     * Set a raw value from the database, automatically converting to the right type
     * @param key The column key
     * @param rawValue The raw value from the database
     * @returns The map instance for chaining
     */
    setRaw<K extends BackendColumns>(key: K, rawValue: any): this {
      // Convert the raw value to the appropriate type
      let processedValue: any = rawValue;
      
      // Date conversion for appropriate column types
      // Note: This is more straightforward than type checking at runtime
      // but requires maintenance if new date columns are added
      const dateColumnNames = [
        'started_at', 'created_at', 'scheduled_start', 'opened_at',
        'customer_joined_at', 'customer_live_at', 'customer_live_known_at', 'first_user_joined_at',
        'callback_timing'
      ];
      
      if (dateColumnNames.some(dateCol => key.toString().includes(dateCol))) {
        // Handle date conversion, ensuring we pass a number to jsonSafeValueToDate
        if (typeof rawValue === 'number') {
          processedValue = jsonSafeValueToDate(rawValue);
        } else if (typeof rawValue === 'string' && !isNaN(Number(rawValue))) {
          processedValue = jsonSafeValueToDate(Number(rawValue));
        }
      }
      
      return this.set(key, processedValue);
    }
  }

  /**
   * Define the response type with a strongly typed Map
   * When using .get() on the result map, the return type will be inferred correctly
   * based on the column key used
   */
  export type SessionListResponse = {
    session_id: string;
    start: Date;
    customer_address: string | null;
    agent_address: string | null;
    user_id: string | null;
    conversation_type: ConversationType;
    result: TypedResultMap;
  }

  export function jsonSafeToSessionListResponse(value: Record<string, any>[], select: SelectColumn[]): SessionListResponse[] {
    return value.map((row) => {
        const session_id_column = select.findIndex((c) => c.column_name === SessionV2Columns.SESSION_ID)
        const start_column = select.findIndex((c) => c.column_name === SessionV2Columns.STARTED_AT)
        const conversation_type_column = select.findIndex((c) => c.column_name === SessionV2Columns.CONVERSATION_TYPE)
        if ([session_id_column, start_column, conversation_type_column].some((col) => col === -1)) {
            throw new Error("Missing required columns in the response");
        }
        
        const session_id = row[session_id_column]
        const start = jsonSafeValueToDate(row[start_column])
        const conversation_type = row[conversation_type_column] as ConversationType

        const agent_address_column = select.findIndex((c) => c.column_name === SessionV2Columns.AGENT_ADDRESS)
        const agent_address = agent_address_column === -1 ? null : row[agent_address_column] as string

        const customer_address_column = select.findIndex((c) => c.column_name === SessionV2Columns.CUSTOMER_ADDRESS)
        const customer_address = customer_address_column === -1 ? null : row[customer_address_column] as string

        const user_id_column = select.findIndex((c) => c.column_name === SessionV2Columns.RESOURCE_ID)
        const user_id = user_id_column === -1 ? null : row[user_id_column] as string

        const result = new TypedResultMap()
        
        for (let i = 0; i < select.length; i++) {
            const column = select[i]
            const columnName = column.column_name as BackendColumns
            
            // Use the new setRaw method to handle type conversions automatically
            result.setRaw(columnName, row[i])
        }
        
        return {
            session_id: session_id,
            start: start,
            conversation_type: conversation_type,
            customer_address: customer_address,
            agent_address: agent_address,
            user_id: user_id,
            result: result
      }
    })
}


export type SessionDataResult = {
  session: SessionV2,
  session_agents: SessionAgents | null,
  session_details: SessionDetailsV2 | null,
  custom_metric: CustomMetric | null,
  custom_detail: CustomDetail | null,
  prospect_info: ProspectInfo | null,
  comments: CommentDataResult[]
  call_metric: CallMetric | null
  sales_metric: SessionMetric | null
  counterpart_data: CounterpartData | null
  turns: SpeechTurn[]
  review_status: ReviewStatus | null
  session_state: SessionState | null
}

export function jsonSafeToSessionDataResponse(value: Record<string, any>): SessionDataResult {
  let session_state = null
  try {
    session_state = value.session_states ? jsonSafeToSessionState(value.session_states) : null
  } catch (e) {
    console.error('error parsing session state', e)
    session_state = null
  }
  
  return {
    session: jsonSafeToSessionV2(value.session),
    session_agents: value.session_agents ? jsonSafeToSessionAgents(value.session_agents) : null,
    session_details: value.session_details ? jsonSafeToSessionDetailsV2(value.session_details) : null,
    custom_metric: value.custom_metric ? jsonSafeToCustomMetric(value.custom_metric) : null,
    custom_detail: value.custom_detail ? jsonSafeToCustomDetail(value.custom_detail) : null,
    prospect_info: value.prospect_info ? jsonSafeToProspectInfo(value.prospect_info) : null,
    comments: value.comments.map((comment: Record<string, any>) => jsonSafeToComment(comment)),
    call_metric: value.call_metric ? jsonSafeToCallMetric(value.call_metric) : null,
    sales_metric: value.sales_metric ? jsonSafeToSessionMetric(value.sales_metric) : null,
    counterpart_data: value.counterpart_data ? jsonSafeToCounterpartData(value.counterpart_data) : null,
    turns: value.turns.map((turn: Record<string, any>) => ({
      role: turn[0] ? PartyRole.PROSPECT : PartyRole.REP,
      text: turn[1],
      start: turn[2],
      end: turn[3]
    })),
    review_status: value.review_status ? jsonSafeToReviewStatus(value.review_status) : null,
    session_state: session_state
  }
}
////////////////////////////////////////////
// UserSessionResult is the return type of `list-user-sessions`
////////////////////////////////////////////

export type CommentDataResult = {
  comment_id: string
  session_id: string
  user_id: string
  comment: string
  created_at: Date
  user_name: string
  start: number | null
  end: number | null
  transcriptIdx?: number
  isPending?: boolean
}

export enum UpdateType {
  MODIFY = 'MODIFY',
  REMOVE = 'REMOVE',
  ADD = 'ADD'
}

export type CommentUpdate = {
  commentId: string
  comment: string
  start?: number
  end?: number
}

export type CommentParams = {
  comment: CommentUpdate
  update: UpdateType
}

export function jsonSafeToComment(value: Record<string, any>): CommentDataResult {
  return {
    'comment_id': value.comment_id,
    'session_id': value.session_id,
    'user_id': value.user_id,
    'comment': value.comment,
    'created_at': jsonSafeValueToDate(value.created_at),
    'start': value.start,
    'end': value.end,
    'user_name': value.user_name
  }
}

export type ReviewStatus = {
  session_id: string
  opening_user_id: string
  is_open: boolean
  opened_at: Date
}

export function jsonSafeToReviewStatus(value: Record<string, any>): ReviewStatus {    
  return {
    session_id: value.session_id,
    opening_user_id: value.opening_user_id,
    is_open: value.is_open,
    opened_at: jsonSafeValueToDate(value.opened_at)
  }
}

export type AudioDataResult = {
  session: SessionV2
  audio: AudioResult[]
  audio_links: string[]
}

export function jsonSafeToAudioDataResult(value: Record<string, any>): AudioDataResult {
  return {
    session: jsonSafeToSessionV2(value.session),
    audio: value.audio.map((x: Record<string, any>) => jsonSafeToAudio(x)),
    audio_links: value.audio_links
  }
}
function jsonSafeToAudio(value: Record<string, any>): AudioResult {
  return {
    audio_id: value.audio_id,
    conversation_id: value.conversation_id,
    party_code: value.party_code,
    start: jsonSafeValueToDate(value.start),
    end: jsonSafeValueToDate(value.end),
    bucket_key: value.bucket_key,
    audio_status: value.audio_status
  }
}

export type AudioResult = {
  audio_id: string
  conversation_id: string
  party_code: number
  start: Date
  end: Date
  bucket_key: string
  audio_status: string
}
export type TranscriptContent = {
  'personName': string
  'partyCode': number
  'content': string
  'start': number // in milliseconds
  'end'?: number // in milliseconds, only specified sometimes
}
// Other data types used by services

export type Word = {
  start_time: number
  end_time: number
  text: string
  confidence: number | null
}


export type SpeechTurn = {
  start: number
  end: number
  text: string
  role: PartyRole,
}

export function jsonSafeToSpeechTurn(x: Record<string, any>): SpeechTurn {
  return {
    'start': x.start,
    'end': x.end,
    'text': x.text,
    'role': x.role
  }
}
